import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import Star from './Star';
import styles from './RatingSelector.css';

const HIGHEST_RATING = 5;
const LOWEST_RATING = 1;

// DOM order is ascending [1..5] so it matches both visual order and the
// values that assistive tech announces as position info. The hover cascade
// (light up stars 1..hovered) is driven by `hoveredStar` state rather than a
// CSS sibling selector, since reversed-direction sibling selectors require
// :has() which isn't supported on the project's Chrome 59 target.
const STARS = Array.from(
  { length: HIGHEST_RATING },
  (_, i) => LOWEST_RATING + i
);

export class RatingSelector extends React.PureComponent {
  static propTypes = {
    className: PropTypes.string,
    id: PropTypes.string,
    onChange: PropTypes.func,
    rating: PropTypes.number,
    testId: PropTypes.string,
    'aria-describedby': PropTypes.string,
    'aria-invalid': PropTypes.bool,
    'aria-labelledby': PropTypes.string
  };

  static defaultProps = {
    onChange: () => {},
    rating: 0
  };

  state = { hoveredStar: 0 };

  activeStarRef = React.createRef();

  handleStarHover = (hoveredStar) => this.setState({ hoveredStar });

  handleStarLeave = () => this.setState({ hoveredStar: 0 });

  // Imperative focus(): radiogroup container is non-focusable per the
  // ARIA APG radio-group pattern, so callers (e.g. ReviewEdition opening
  // the form) move focus into the active radio child instead.
  // eslint-disable-next-line react/no-unused-class-component-methods
  focus = () => {
    if (this.activeStarRef.current) {
      this.activeStarRef.current.focus();
    }
  };

  handleStarClick = (rating) => {
    this.props.onChange(rating);
  };

  handleKeyDown = (event) => {
    const { rating, onChange } = this.props;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        if (rating < HIGHEST_RATING) {
          onChange(rating + 1);
        }
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        if (rating > LOWEST_RATING) {
          onChange(rating - 1);
        }
        break;
      case 'Home':
        event.preventDefault();
        if (rating !== LOWEST_RATING) {
          onChange(LOWEST_RATING);
        }
        break;
      case 'End':
        event.preventDefault();
        if (rating !== HIGHEST_RATING) {
          onChange(HIGHEST_RATING);
        }
        break;
      default:
        break;
    }
  };

  render() {
    const { id, className, testId, rating } = this.props;
    const tabStopValue = rating || LOWEST_RATING;
    const ariaLabelledBy = this.props['aria-labelledby'];

    return (
      // ARIA APG radio-group: focus lives on a child radio via roving
      // tabindex, not on the group container.
      // eslint-disable-next-line jsx-a11y/interactive-supports-focus
      <span
        id={id}
        role="radiogroup"
        // Prefer the visible label binding when callers provide one (WCAG
        // 1.3.1); fall back to the static aria-label otherwise.
        aria-label={ariaLabelledBy ? undefined : 'Edit rating'}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={this.props['aria-describedby']}
        aria-invalid={this.props['aria-invalid']}
        className={classNames(className, styles.rating)}
        data-test-id={testId}
        onKeyDown={this.handleKeyDown}
      >
        {STARS.map((starValue) => {
          const isTabStop = tabStopValue === starValue;
          const { hoveredStar } = this.state;
          const isHighlighted = hoveredStar
            ? starValue <= hoveredStar
            : rating >= starValue;

          return (
            <Star
              key={starValue}
              ref={isTabStop ? this.activeStarRef : null}
              value={starValue}
              outOf={HIGHEST_RATING}
              selected={rating === starValue}
              highlighted={isHighlighted}
              isTabStop={isTabStop}
              onClick={this.handleStarClick}
              onHover={this.handleStarHover}
              onLeave={this.handleStarLeave}
            />
          );
        })}
      </span>
    );
  }
}

export default React.forwardRef((props, ref) => (
  <RatingSelector ref={ref} {...props} />
));
